export const PlanCycle = {
  MONTHLY: "MONTHLY",
  YEARLY: "YEARLY",
} as const;

export type PlanCycleValue = (typeof PlanCycle)[keyof typeof PlanCycle];

export const HOMOLOGATION_MONTHS = 12;
export const DEFAULT_MAX_PIX_INSTALLMENTS = 12;
export const MIN_PIX_INSTALLMENTS = 2;
export const CONSTELLATION_PRICE_MIN = 152;
export const CONSTELLATION_PRICE_MAX = 1800;
export const CONSTELLATION_SOCIAL_PRICE = 152;
export const CONSTELLATION_PLATFORM_PRICE = 350;

export const ConstellationOffer = {
  SOCIAL: "social",
  PLATFORM: "plataforma",
  BOTH: "ambos",
} as const;

export type ConstellationOfferValue = (typeof ConstellationOffer)[keyof typeof ConstellationOffer];
export type ConstellationVitrineOffer =
  | typeof ConstellationOffer.SOCIAL
  | typeof ConstellationOffer.PLATFORM;

export type TherapistConstellationFields = {
  constellationOffer?: string | null;
  constellationPrice?: number | null;
};

export function parseVitrineOffer(value?: string | null): ConstellationVitrineOffer | null {
  const offer = String(value || "").trim();
  if (offer === ConstellationOffer.SOCIAL) return ConstellationOffer.SOCIAL;
  if (offer === ConstellationOffer.PLATFORM) return ConstellationOffer.PLATFORM;
  return null;
}

export function resolveConstellationOffer(
  data?: TherapistConstellationFields | null,
): ConstellationOfferValue | null {
  const offer = String(data?.constellationOffer || "").trim();
  if (offer === ConstellationOffer.BOTH) return ConstellationOffer.BOTH;
  if (offer === ConstellationOffer.SOCIAL) return ConstellationOffer.SOCIAL;
  if (offer === ConstellationOffer.PLATFORM) return ConstellationOffer.PLATFORM;
  const price = Number(data?.constellationPrice);
  if (price === CONSTELLATION_SOCIAL_PRICE) return ConstellationOffer.SOCIAL;
  if (Number.isFinite(price) && price > 0) return ConstellationOffer.PLATFORM;
  return null;
}

export function appearsInSocialVitrine(data?: TherapistConstellationFields | null): boolean {
  const offer = resolveConstellationOffer(data);
  return offer === ConstellationOffer.SOCIAL || offer === ConstellationOffer.BOTH;
}

export function appearsInPlatformVitrine(data?: TherapistConstellationFields | null): boolean {
  return resolveConstellationOffer(data) !== ConstellationOffer.SOCIAL;
}

export function isSocialConstellationOffer(data?: TherapistConstellationFields | null): boolean {
  return appearsInSocialVitrine(data);
}

export function offerFromVitrineFlags(social: boolean, platform: boolean): ConstellationOfferValue | null {
  if (social && platform) return ConstellationOffer.BOTH;
  if (social) return ConstellationOffer.SOCIAL;
  if (platform) return ConstellationOffer.PLATFORM;
  return null;
}

export function vitrineFlagsFromOffer(data?: TherapistConstellationFields | null): {
  social: boolean;
  platform: boolean;
} {
  const offer = resolveConstellationOffer(data);
  return {
    social: offer === ConstellationOffer.SOCIAL || offer === ConstellationOffer.BOTH,
    platform: offer === ConstellationOffer.PLATFORM || offer === ConstellationOffer.BOTH,
  };
}

export function priceForConstellationOffer(offer: ConstellationOfferValue | null): number | null {
  if (offer === ConstellationOffer.SOCIAL) return CONSTELLATION_SOCIAL_PRICE;
  if (offer === ConstellationOffer.PLATFORM || offer === ConstellationOffer.BOTH) {
    return CONSTELLATION_PLATFORM_PRICE;
  }
  return null;
}

export function priceFromTherapistConstellation(
  data?: TherapistConstellationFields | null,
  requestedOffer?: string | null,
): number | null {
  const offer = resolveConstellationOffer(data);
  if (!offer) return null;
  if (offer === ConstellationOffer.SOCIAL) return CONSTELLATION_SOCIAL_PRICE;
  if (offer === ConstellationOffer.PLATFORM) return CONSTELLATION_PLATFORM_PRICE;
  return parseVitrineOffer(requestedOffer) === ConstellationOffer.SOCIAL
    ? CONSTELLATION_SOCIAL_PRICE
    : CONSTELLATION_PLATFORM_PRICE;
}

export type PlanCommerceFields = {
  isRecurring?: boolean;
  cycle?: string | null;
  allowPixInstallments?: boolean;
  maxPixInstallments?: number;
  cancellable?: boolean;
  homologationMonths?: number;
  homologationOnly?: boolean;
  value?: number;
  userType?: string;
  name?: string;
  sessionLabel?: string;
};

export function roundMoney(value: number): number {
  return Math.round(Number(value || 0) * 100) / 100;
}

export function normalizePlanCycle(cycle?: string | null): PlanCycleValue {
  return cycle === PlanCycle.YEARLY ? PlanCycle.YEARLY : PlanCycle.MONTHLY;
}

export function homologationCoveredMonths(plan: PlanCommerceFields): number {
  if (normalizePlanCycle(plan.cycle) === PlanCycle.YEARLY) return HOMOLOGATION_MONTHS;
  const months = Number(plan.homologationMonths || 0);
  return months >= HOMOLOGATION_MONTHS ? HOMOLOGATION_MONTHS : 0;
}

export function planRequiresHomologation(plan: PlanCommerceFields): boolean {
  return homologationCoveredMonths(plan) >= HOMOLOGATION_MONTHS || plan.cancellable === false;
}

export function planIsCancellable(plan: PlanCommerceFields): boolean {
  if (plan.cancellable === false) return false;
  return !planRequiresHomologation(plan);
}

export function planAllowsPixInstallments(plan: PlanCommerceFields): boolean {
  if (planRequiresHomologation(plan)) return true;
  if (plan.isRecurring) return false;
  return !!plan.allowPixInstallments;
}

export function planPixAvailable(plan: PlanCommerceFields): boolean {
  return !plan.isRecurring || planAllowsPixInstallments(plan);
}

export function maxPixInstallmentsForPlan(plan: PlanCommerceFields): number {
  const max = Number(plan.maxPixInstallments || DEFAULT_MAX_PIX_INSTALLMENTS);
  if (!Number.isFinite(max)) return DEFAULT_MAX_PIX_INSTALLMENTS;
  return Math.min(DEFAULT_MAX_PIX_INSTALLMENTS, Math.max(1, Math.floor(max)));
}

export function pixInstallmentOptions(plan: PlanCommerceFields): number[] {
  if (!planAllowsPixInstallments(plan)) return [1];
  const max = maxPixInstallmentsForPlan(plan);
  const options = [1, 3, 6, 12].filter((n) => n === 1 || n <= max);
  if (max > 1 && !options.includes(max)) options.push(max);
  return options.sort((a, b) => a - b);
}

/** Valor total do compromisso (ano homologado ou preço do plano). */
export function planCommitmentTotal(plan: PlanCommerceFields): number {
  const value = roundMoney(Number(plan.value || 0));
  if (normalizePlanCycle(plan.cycle) === PlanCycle.YEARLY) return value;
  const months = homologationCoveredMonths(plan);
  if (months > 1) return roundMoney(value * months);
  return value;
}

export function resolvePixInstallmentValues(
  plan: PlanCommerceFields,
  installmentCount: number,
): { totalValue: number; parcelValue: number; installmentCount: number } {
  const totalValue = planCommitmentTotal(plan);
  const count = Math.max(1, Math.floor(Number(installmentCount) || 1));
  return {
    totalValue,
    parcelValue: roundMoney(totalValue / count),
    installmentCount: count,
  };
}

export function formatConstellationPrice(value?: number | null): string | null {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  return amount.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

export function isPatientConstellationPlan(plan: Pick<PlanCommerceFields, "userType" | "name" | "sessionLabel">): boolean {
  if ((plan.userType || "").toLowerCase() !== "patient") return false;
  const blob = `${plan.name || ""} ${plan.sessionLabel || ""}`.toLowerCase();
  if (/particip|represent|clube/.test(blob)) return false;
  const label = (plan.sessionLabel || "").toLowerCase();
  return /constel/.test(blob) || label.includes("constel");
}

function foldPlanName(name?: string): string {
  return (name || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .trim();
}

/** Nome do plano social de sessão. O rótulo "Constelação" sozinho não basta: um evento também pode ter esse rótulo. */
export function isSocialConstellationPlan(plan: Pick<PlanCommerceFields, "name" | "sessionLabel" | "userType">): boolean {
  if (plan.userType && plan.userType.toLowerCase() !== "patient") return false;
  return /valor social|\bsocial\b/.test(foldPlanName(plan.name));
}

/** Nome do plano de sessão da plataforma: "Sua Constelação" ou um plano cujo nome começa com Constelação. */
export function isPlatformConstellationPlan(plan: Pick<PlanCommerceFields, "name" | "userType">): boolean {
  if (plan.userType && plan.userType.toLowerCase() !== "patient") return false;
  const name = foldPlanName(plan.name);
  if (/valor social|\bsocial\b|particip|represent|clube/.test(name)) return false;
  return /sua constela/.test(name) || /^constela/.test(name);
}

export function pickSocialConstellationPlan<T extends Pick<PlanCommerceFields, "userType" | "name" | "sessionLabel">>(
  plans: T[],
): T | undefined {
  return plans.find((plan) => (plan.userType || "").toLowerCase() === "patient" && isSocialConstellationPlan(plan));
}

export function pickPlatformConstellationPlan<T extends Pick<PlanCommerceFields, "userType" | "name">>(
  plans: T[],
): T | undefined {
  const patients = plans.filter((plan) => (plan.userType || "").toLowerCase() === "patient");
  return (
    patients.find((plan) => /sua constela/.test(foldPlanName(plan.name))) ||
    patients.find((plan) => isPlatformConstellationPlan(plan))
  );
}

/** Preço exibido e cobrado vem do plano de sessão ativo. 152/350 só entram se esse plano ainda não carregou. */
export function constellationPricesFromPlans(
  plans: Array<Pick<PlanCommerceFields, "userType" | "name" | "sessionLabel" | "value">>,
): { social: number; platform: number } {
  const social = pickSocialConstellationPlan(plans);
  const platform = pickPlatformConstellationPlan(plans);
  const socialValue = Number(social?.value);
  const platformValue = Number(platform?.value);
  return {
    social: Number.isFinite(socialValue) && socialValue > 0 ? socialValue : CONSTELLATION_SOCIAL_PRICE,
    platform: Number.isFinite(platformValue) && platformValue > 0 ? platformValue : CONSTELLATION_PLATFORM_PRICE,
  };
}
